import { useUser } from '@folklore/auth';
import { useTracking } from '@folklore/tracking';
import { useQuery, keepPreviousData as previousPlaceholder } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import isArray from 'lodash-es/isArray';
import isFunction from 'lodash-es/isFunction';
import {
    createContext,
    ReactNode,
    startTransition,
    use,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useCurrentDocument } from '../hooks/useDocument';

import defaultPromotions from '../data/promotions';
import { useApi } from './ApiContext';
import { useSite } from './SiteContext';

// import q95Sondage from '../assets/img/promotions/quatre95_sondage_2024.png';

interface PromotionsContextValue {
    promotions: Promotion[];
    allPromotions: Promotion[];
    matchedPromotions: Promotion[];
    markAsDone: (id: string) => void;
    addPromotion: (promotion: Promotion) => void;
    removePromotion: (id: string) => void;
}

export const PromotionsContext = createContext<PromotionsContextValue>({
    promotions: [],
    allPromotions: [],
    matchedPromotions: [],
    markAsDone: () => {},
    addPromotion: () => {},
    removePromotion: () => {},
});

export function prefetchPromotions(queryClient, api, site) {
    const { handle: siteHandle } = site;
    return queryClient.prefetchQuery({
        queryKey: ['promotions', siteHandle],
        queryFn: ({ queryKey: [, siteParam], signal }) =>
            api.promotions.get(
                {
                    site: siteParam,
                },
                null,
                null,
                { signal },
            ),
        staleTime: 1000 * 60 * 30,
    });
}

export function usePromotionsQuery(opts: { keepPreviousData?: boolean } = {}) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { handle: siteHandle } = useSite();
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['promotions', siteHandle],
        queryFn: ({ queryKey: [, siteParam], signal }) =>
            api.promotions.get(
                {
                    site: siteParam,
                },
                null,
                null,
                { signal },
            ),
        staleTime: 1000 * 60 * 30,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export function usePromotions() {
    const context = use(PromotionsContext);
    return context;
}

export function useMatchedPromotions() {
    const { matchedPromotions } = usePromotions();
    return matchedPromotions || [];
}

interface PromotionsProviderProps {
    fixedPromotions?: Promotion[];
    children: ReactNode;
}

const defaultFixedPromotions = [...defaultPromotions];

export function PromotionsProvider({
    fixedPromotions = defaultFixedPromotions,
    children,
}: PromotionsProviderProps) {
    const { handle: siteHandle, settings = null } = useSite();
    const { items: loadedPromotions = null } = usePromotionsQuery();
    const [runtimePromotions, setRuntimePromotions] = useState([]);
    const { subscriptions: userSubscriptions = null } = useUser() || {};
    const currentDocument = useCurrentDocument();
    const [promotionDocument, setPromotionDocument] = useState(currentDocument);
    useEffect(() => {
        startTransition(() => {
            setPromotionDocument(currentDocument);
        });
    }, [currentDocument]);
    const promotions = useMemo(
        () =>
            [...(fixedPromotions ?? []), ...(loadedPromotions ?? []), ...(runtimePromotions ?? [])]
                .filter(({ site = null }) => site === null || site === siteHandle)
                .filter(
                    ({ check = null }) =>
                        !isFunction(check) ||
                        check({
                            currentDocument: promotionDocument,
                            settings,
                        }),
                )
                .reduce((uniquePromotions, promotion) => {
                    if (uniquePromotions.findIndex(({ id }) => id === promotion.id) === -1) {
                        return [...uniquePromotions, promotion];
                    }
                    return uniquePromotions;
                }, []),
        [
            fixedPromotions,
            loadedPromotions,
            runtimePromotions,
            promotionDocument,
            siteHandle,
            settings,
        ],
    );

    const [donePromotions, setDonePromotions] = useState(() =>
        (Cookies.get('promotions_done') || '').split(',').filter((it) => it !== ''),
    );

    const userSubscribed = (userSubscriptions || [])
        .filter(({ subscribed = false }) => subscribed)
        .map(({ id }) => id);

    const markAsDone = (id) => {
        const newDonePromotions =
            donePromotions.indexOf(id) === -1 ? [...donePromotions, id] : donePromotions;
        setDonePromotions(newDonePromotions);
        Cookies.set('promotions_done', newDonePromotions.join(','), {
            expires: 365,
        });
    };

    const filteredPromotions = (promotions || [])
        .filter(({ id, subscription = null, subscriptions = null, force = false }) => {
            if (!force && donePromotions.indexOf(id) !== -1) {
                return false;
            }
            const finalSubscriptions = [
                ...(isArray(subscriptions) ? subscriptions : [subscriptions]),
                ...(isArray(subscription) ? subscription : [subscription]),
            ].filter((it) => it !== null);

            const isSubscribed =
                finalSubscriptions.findIndex((it) => userSubscribed.indexOf(it) !== -1) !== -1;
            if (finalSubscriptions.length > 0 && isSubscribed) return false;
            return true;
        })
        .sort(({ priority: aPriority = 0 }, { priority: bPriority = 0 }) => {
            if (aPriority === bPriority) {
                return 0;
            }
            return aPriority > bPriority ? -1 : 1;
        });

    const [matchedPromotions, setMatchedPromotions] = useState([]);
    const tracking = useTracking();
    useEffect(() => {
        const matchPromotions = promotions.filter(
            ({ matchTrigger = null }) => matchTrigger !== null,
        );
        const currentTriggers = promotions.map(({ id }) => `promotion_match_${id}`);
        tracking.triggers.getRegistered().forEach((trigger: string) => {
            if (
                trigger.match(/^promotion_match_/) !== null &&
                currentTriggers.indexOf(trigger) === -1
            ) {
                tracking.triggers.unregister(trigger);
            }
        });
        matchPromotions
            .filter(({ id }) => !tracking.triggers.isRegistered(`promotion_match_${id}`))
            .forEach((promotion) => {
                const { id, matchTrigger } = promotion;
                tracking.triggers.register(`promotion_match_${id}`, matchTrigger, () => {
                    setMatchedPromotions((currentPromotions) =>
                        [
                            ...currentPromotions.filter(({ id: currentId }) => id !== currentId),
                            {
                                ...promotion,
                                matchTime: new Date().getTime(),
                            },
                        ].sort(
                            (
                                { priority: aPriority = 0, matchTime: aMatchTime = 0 },
                                { priority: bPriority = 0, matchTime: bMatchTime = 0 },
                            ) => {
                                if (aPriority === bPriority) {
                                    return aMatchTime > bMatchTime ? -1 : 1;
                                }
                                return aPriority > bPriority ? -1 : 1;
                            },
                        ),
                    );
                });
            });
    }, [filteredPromotions, tracking]);

    useEffect(
        () => () => {
            promotions
                .filter(({ matchTrigger = null }) => matchTrigger !== null)
                .forEach(({ id }) => {
                    tracking.triggers.unregister(`promotion_popup_${id}`);
                });
        },
        [],
    );

    const addPromotion = (promotion) => {
        setRuntimePromotions((currentPromotions) => [
            ...currentPromotions.filter(({ id }) => promotion.id !== id),
            promotion,
        ]);
    };

    const removePromotion = (removeId) => {
        setRuntimePromotions((currentPromotions) =>
            currentPromotions.filter(({ id }) => removeId !== id),
        );
    };

    const value = {
        promotions: filteredPromotions,
        allPromotions: promotions,
        matchedPromotions,
        markAsDone,
        addPromotion,
        removePromotion,
    };

    return <PromotionsContext value={value}>{children}</PromotionsContext>;
}
